// @ts-nocheck
/*
 * Port of session-replay/replayUtils.js (212 lines). BYTE-IDENTICAL.
 * Shared helpers for the replay player + analytics:
 *   - `decodeChunk` — gzip+base64 back into rrweb events (matches the
 *     transport encoding on the recorder side).
 *   - `deriveJourney` — ordered screen visits with dwell/click totals.
 *   - `computeIdleRanges` — pairs idle_start/idle_end into [{start,end}].
 *   - `deriveSessionInsights` — one-shot text summaries drawn only from
 *     the semantic timeline (no extra API call).
 *   - `describeEvent` — human label for a timeline row.
 * `MARKER_COLORS` is the palette shared with TimelineScrubber and
 * SemanticTimeline dots.
 */
import pako from 'pako';

export function formatDuration(totalSeconds: number) {
  const s = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

/** Decode one stored chunk (gzip+base64) back into rrweb events. */
export function decodeChunk(chunk: any) {
  try {
    const binary = atob(chunk.payload);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const json = pako.ungzip(bytes, { to: 'string' });
    const events = JSON.parse(json);
    return Array.isArray(events) ? events : [];
  } catch {
    return [];
  }
}

/**
 * Derive a journey (ordered screen visits with dwell + click counts) from the
 * semantic event timeline.
 */
export function deriveJourney(events: any[], sessionEndTs: any = null) {
  const ordered = [...(events || [])].sort(
    (a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime(),
  );
  const steps: any[] = [];
  let current: any = null;

  for (const e of ordered) {
    if (e.event_type === 'screen_change') {
      if (current) {
        current.left_at = e.ts;
        current.dwell_seconds = Math.max(
          0,
          (new Date(e.ts).getTime() - new Date(current.entered_at).getTime()) / 1000,
        );
      }
      current = {
        screen: e.screen || 'Unknown',
        entered_at: e.ts,
        left_at: null,
        dwell_seconds: 0,
        clicks: 0,
        rage_clicks: 0,
      };
      steps.push(current);
    } else if (current) {
      if (e.event_type === 'click') current.clicks += 1;
      if (e.event_type === 'rage_click') current.rage_clicks += 1;
    }
  }

  if (current) {
    const end = sessionEndTs || ordered[ordered.length - 1]?.ts;
    if (end) {
      current.dwell_seconds = Math.max(
        0,
        (new Date(end).getTime() - new Date(current.entered_at).getTime()) / 1000,
      );
    }
  }
  return steps;
}

/** Idle ranges [{start, end}] derived from idle_start / idle_end events. */
export function computeIdleRanges(events: any[]) {
  const ranges: any[] = [];
  let openStart: any = null;
  for (const e of [...(events || [])].sort(
    (a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime(),
  )) {
    if (e.event_type === 'idle_start') openStart = e.ts;
    if (e.event_type === 'idle_end' && openStart) {
      ranges.push({ start: openStart, end: e.ts });
      openStart = null;
    }
  }
  return ranges;
}

export const MARKER_COLORS: any = {
  click: '#3b82f6',
  rage_click: '#ef4444',
  screen_change: '#8b5cf6',
  api_call: '#f59e0b',
  idle_start: '#9ca3af',
  modal_open: '#10b981',
};

export function markerColor(eventType: string) {
  return MARKER_COLORS[eventType] || '#6b7280';
}

export function parsePayload(event: any) {
  try {
    return typeof event.payload === 'string'
      ? JSON.parse(event.payload)
      : event.payload || {};
  } catch {
    return {};
  }
}

/**
 * Per-session auto insights: "spent 42% on Reports", "switched 18 times",
 * "idle 12 min", "most interacted: X". Derived purely from the semantic
 * timeline — no extra API call needed.
 */
export function deriveSessionInsights(session: any, events: any[], journey: any[]) {
  const insights: any[] = [];
  if (!session) return insights;

  const totalDwell = (journey || []).reduce((n: number, s: any) => n + s.dwell_seconds, 0);
  const top = [...(journey || [])].sort(
    (a: any, b: any) => b.dwell_seconds - a.dwell_seconds,
  )[0];
  if (top && totalDwell > 0) {
    const pct = Math.round((top.dwell_seconds / totalDwell) * 100);
    insights.push({
      type: 'screen_focus',
      text: `Agent spent ${pct}% of the session on ${top.screen}.`,
    });
  }

  const switches = (events || []).filter((e: any) => e.event_type === 'screen_change').length;
  if (switches > 1) {
    insights.push({
      type: 'screen_switching',
      text: `Agent switched screens ${switches} times.`,
    });
  }

  if (session.idle_seconds > 0 && session.duration_seconds > 0) {
    const pct = Math.round((session.idle_seconds / session.duration_seconds) * 100);
    insights.push({
      type: 'idle',
      text: `Agent was idle for ${formatDuration(session.idle_seconds)} (${pct}% of the session).`,
    });
  }

  const clickCounts = new Map();
  for (const e of events || []) {
    if (e.event_type === 'click' && e.element_label) {
      clickCounts.set(e.element_label, (clickCounts.get(e.element_label) || 0) + 1);
    }
  }
  const topElement = [...clickCounts.entries()].sort((a, b) => b[1] - a[1])[0];
  if (topElement) {
    insights.push({
      type: 'top_component',
      text: `Most interacted component: "${topElement[0]}" (${topElement[1]} clicks).`,
    });
  }

  if (session.rage_click_count > 0) {
    const rage = (events || []).find((e: any) => e.event_type === 'rage_click');
    insights.push({
      type: 'ux_issue',
      text: `${session.rage_click_count} rage click${
        session.rage_click_count === 1 ? '' : 's'
      } detected${
        rage?.element_label ? ` — mostly on "${rage.element_label}"` : ''
      }. Potential UX issue.`,
    });
  }

  const failedApi = (events || []).filter((e: any) => {
    if (e.event_type !== 'api_call') return false;
    const p = parsePayload(e);
    return !p.status || p.status >= 400;
  }).length;
  if (failedApi > 0) {
    insights.push({
      type: 'api_health',
      text: `${failedApi} failed request${
        failedApi === 1 ? '' : 's'
      } occurred during this session.`,
    });
  }

  return insights;
}

/** Human label for a timeline row. */
export function describeEvent(event: any) {
  const p = parsePayload(event);
  switch (event.event_type) {
    case 'click':
      return `Clicked ${event.element_label || 'element'}`;
    case 'rage_click':
      return `Rage clicked ${event.element_label || 'element'}`;
    case 'screen_change':
      return `Opened ${event.screen || p.path || 'screen'}`;
    case 'idle_start':
      return 'Went idle';
    case 'idle_end':
      return `Active again (idle ${formatDuration(p.idle_seconds)})`;
    case 'modal_open':
      return `Opened dialog${event.element_label ? ` ${event.element_label}` : ''}`;
    case 'modal_close':
      return 'Closed dialog';
    case 'api_call':
      return `${p.status >= 400 || !p.status ? 'Failed' : 'Slow'} request ${
        p.method || ''
      } ${p.url || ''}`.trim();
    case 'scroll_depth':
      return `Scrolled to ${p.depth_pct ?? '?'}%`;
    case 'tab_blur':
      return 'Left the tab';
    case 'tab_focus':
      return 'Returned to the tab';
    default:
      return event.event_type;
  }
}
